import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import Button from '../../../../components/Form/Button';
import { InfoModal } from '../../../../components/Modal/InfoModal';
import { Icon, Icons } from 'components/Icon';
import { useArchiveCustomer } from 'common/queries-and-mutations/admin/customer';
import CustomSelect from 'components/Form/CustomSelect';
import TextField from 'components/Form/TextField';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';

type Props = {
  onSuccess: () => void;
  onClose: () => void;
  className?: string;
  customerId: number;
};

export default function ArchiveModal({
  onSuccess,
  onClose,
  className = 'w-[20%]',
  customerId
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reason, setReason] = useState<any>('');
  const [date, setDate] = useState('');
  const [file, setFile] = useState<File>();

  const { mutate, isSuccess, isError } = useArchiveCustomer();

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const handleArchive = (e: FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();

    if (!reason) {
      toast.error(
        <Alert
          type="error"
          message="Please enter reason why you want to archive this customer"
        />
      );
      return;
    }
    if (!date) {
      toast.error(<Alert type="error" message="Date is required" />);
      return;
    }
    if (!file && reason === 'Will has been executed') {
      toast.error(
        <Alert type="error" message="Please upload death certificate" />
      );
      return;
    }
    const formData = new FormData();
    formData.append('reason', reason);
    formData.append('date', date);
    if (file) {
      formData.append('file', file);
    }

    mutate({ payload: formData, customerId });
  };

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    setFile(file);
  };

  return (
    <InfoModal width={className} className="h-fit rounded-lg py-2">
      <div className="flex w-full flex-col items-center bg-white font-circular text-2xl font-bold text-[#353535]">
        <form onSubmit={handleArchive} className="w-full">
          <div className="flex items-center justify-between border-b border-[#E5E5E5] p-3 px-5">
            <p className="text-left text-base font-medium">Archive Customer</p>
            <div className="" role="button" onClick={onClose}>
              <Icon name={Icons.Close} />
            </div>
          </div>
          <div className="p-5">
            <div className="group relative z-0 mb-2 mt-3 w-full">
              <CustomSelect
                options={[
                  {
                    value: 'Will has been executed',
                    label: 'Will has been executed'
                  },
                  {
                    value: 'Legal issues & complications',
                    label: 'Legal issues & complications'
                  },
                  {
                    value: 'Others',
                    label: 'Others'
                  }
                ]}
                selectedOption={{
                  value: reason,
                  label: reason
                }}
                handleOptionChange={option => {
                  setReason(option?.value);
                }}
                label="Select Reason"
                name="guardian"
                isSearchable
                className="h-[3.2rem] rounded-xl text-base"
                isRequired
              />
            </div>
            <div className="group relative z-0 mb-2 mt-3 w-full">
              <TextField
                name="date"
                placeholder=""
                value={date}
                onChange={e => setDate(e.target.value)}
                type="date"
                label={'Date of Archive'}
                isRequired
                size="lg"
                className="rounded-md border-none bg-[#C6C6C633]"
                maxDate={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="group relative z-0 mb-2 mt-3 w-full">
              <label
                htmlFor="file_upload"
                className="text-[12px] font-medium text-[#353535]"
              >
                Upload Death Certificate
              </label>
              <div className="relative w-full">
                <input
                  type="file"
                  hidden
                  id="file_upload"
                  accept=".png, .jpg, .jpeg"
                  onChange={handleImage}
                />
                <input
                  type="search"
                  disabled
                  style={{ zIndex: 1 }}
                  value={file?.name}
                  className="block w-full rounded-2xl border-[1.5px] border-[#DEE1E5] bg-gray-50 py-3.5 pr-20 text-sm text-gray-900 focus:border-[#DEE1E5] focus:ring-[#DEE1E5]"
                />
                <label
                  role="button"
                  htmlFor="file_upload"
                  style={{ zIndex: 20 }}
                  className="absolute bottom-0 right-0 z-10 w-[6rem] cursor-pointer rounded-2xl border border-[#7F8B94] bg-[#7F8B944D] px-4 py-[14.8px] text-center text-sm font-medium text-[#5C6B7E] focus:outline-none focus:ring-4 focus:ring-blue-300"
                >
                  Upload
                </label>
              </div>
            </div>
          </div>
          <div className="mr-6 mt-4 flex items-center justify-end gap-4 pb-4">
            <Button
              variant="outline"
              size="md"
              className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal text-primary-800"
              label="Cancel"
              onClick={onClose}
            ></Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="mb-3 w-[10rem] rounded text-sm font-normal text-white"
              label="Archive"
              loading={isSubmitting}
              disabled={isSubmitting}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
